import { AvailabilityStatusEnum } from '../shared/models/availabilityStatusEnum';
import { CarLicenseEnum } from '../shared/models/carLicense.enum';
import {
  CustomCriteriaOnJob,
  CustomCriteriaType,
} from '../shared/models/customCriteria';
import { Customer } from '../shared/models/customer';
import { Department } from '../shared/models/department';
import { Job } from '../shared/models/job';
import { Organisation } from '../shared/models/organisation';
import { SexEnum } from '../shared/models/sex.enum';
import { Shift } from '../shared/models/shift';
import { Transfer } from '../shared/models/transfer';

export function getUsersQuery(
  date: string,
  job: Job,
  organisation: Organisation,
  department: Department,
  customer: Customer,
  isOwnJob = true,
  allowUndecided = false,
  ignoreUserPreferenceDepartmentBlacklist = false,
  jobInPast = false,
  allowUsersWithNoNote = true,
  queryForAutoDirectAccept = false,
  shifts: Shift[],
  search?: string,
  transfer?: Transfer,
): { $and: Record<string, any>[] } {
  const $and: Record<string, any>[] = [
    {
      _id: {
        $in: [...(customer?.pool.activeUserIds || [])],
      },
    },
  ];
  if (job.invitees && job.invitees.length > 0) {
    $and.push({
      _id: { $nin: job.invitees.map((invitee) => invitee.userId) },
    });
  }
  let sexValue =
    transfer?.criteria?.sex !== undefined ? transfer.criteria.sex : job.sex;
  if (sexValue === SexEnum.FEMALE || sexValue === SexEnum.MALE) {
    $and.push({ sex: sexValue });
  }
  if (job.department) {
    let departmentId = job.department.id;
    if (departmentId) {
      if (queryForAutoDirectAccept) {
        $and.push({
          [`customerInformation.${customer._id}.autoBookDepartmentIds`]:
            departmentId,
        });
      } else {
        $and.push({
          [`customerInformation.${customer._id}.departmentIds`]: departmentId,
        });
        if (!ignoreUserPreferenceDepartmentBlacklist) {
          $and.push({
            userPreferenceDepartmentBlacklist: { $ne: departmentId },
          });
        }
      }
    }
    if (
      customer.enableTags &&
      organisation?.customerInformation?.[customer._id]?.tags?.length
    ) {
      let tagIds =
        organisation.customerInformation[customer._id].tags?.map(
          (tag) => tag.id,
        ) || [];
      $and.push({
        [`customerInformation.${customer._id}.tags.id`]: {
          $in: tagIds,
        },
      });
    }
    if (department?.blacklistedUsers?.length) {
      $and.push({
        _id: {
          $nin: department.blacklistedUsers?.map(
            (blacklistedUsers) => blacklistedUsers.id,
          ),
        },
      });
    }
  }
  if (job.jobType) {
    $and.push({ jobTypes: job.jobType });
  }
  let certificates = transfer?.criteria?.certificates
    ? transfer.criteria.certificates.filter(
        (certificateValue) => !!certificateValue,
      )
    : job.certificates
    ? job.certificates.filter((certificateValue) => !!certificateValue)
    : [];
  if (certificates && certificates.length > 0) {
    $and.push({
      'certificates.id': {
        $in: certificates.map((certificate) => certificate.id),
      },
    });
  }
  let specialities = transfer?.criteria?.specialities
    ? transfer.criteria.specialities.filter(
        (specialityValue) => !!specialityValue,
      )
    : job.specialities
    ? job.specialities.filter((specialityValue) => !!specialityValue)
    : [];
  if (specialities && specialities.length > 0) {
    $and.push({
      'specialities.id': {
        $in: specialities.map((speciality) => speciality.id),
      },
    });
  }
  let dateAndShiftAvailableFilter = {};
  if (shifts && shifts.length > 0) {
    if (allowUndecided && !jobInPast) {
      dateAndShiftAvailableFilter = {
        $and: [
          ...shifts.map((shift) => {
            return {
              $or: [
                {
                  // is avaliable
                  availabilities: {
                    $elemMatch: {
                      date: date,
                      ['shifts.' + shift.shift]:
                        AvailabilityStatusEnum.AVAILABLE,
                    },
                  },
                },
                {
                  // has unknown availability and shift is in the user default shifts
                  availabilities: {
                    $not: {
                      $elemMatch: {
                        date: date,
                        ['shifts.' + shift.shift]: {
                          $in: [
                            AvailabilityStatusEnum.UNAVAILABLE,
                            AvailabilityStatusEnum.WORKING,
                          ],
                        },
                      },
                    },
                  },
                  shifts: shift.shift,
                },
              ],
            };
          }),
        ],
      };
    } else if (!jobInPast) {
      dateAndShiftAvailableFilter = {
        $and: [
          ...shifts.map((shift) => {
            return {
              availabilities: {
                $elemMatch: {
                  date: date,
                  ['shifts.' + shift.shift]: AvailabilityStatusEnum.AVAILABLE,
                  ...(allowUsersWithNoNote
                    ? {}
                    : {
                        $or: [
                          { note: { $exists: false } },
                          { note: null },
                          { note: '' },
                        ],
                      }),
                },
              },
            };
          }),
        ],
      };
    } else {
      dateAndShiftAvailableFilter = {
        $and: [
          ...shifts.map((shift) => {
            return {
              availabilities: {
                $not: {
                  $elemMatch: {
                    date: date,
                    ['shifts.' + shift.shift]: AvailabilityStatusEnum.WORKING,
                  },
                },
              },
            };
          }),
        ],
      };
    }
  }
  $and.push(dateAndShiftAvailableFilter);

  if (
    job.language !== undefined ||
    (!isOwnJob && transfer?.criteria?.language !== undefined)
  ) {
    const languageValue =
      transfer?.criteria?.language !== undefined
        ? transfer.criteria.language
        : job.language;
    if (languageValue !== null) {
      $and.push({
        languages: languageValue,
      });
    }
  }
  if (job.car !== undefined || transfer?.criteria?.car !== undefined) {
    const carValue =
      transfer?.criteria?.car !== undefined ? transfer.criteria.car : job.car;
    if (carValue === CarLicenseEnum.LICENSE_AVAILABLE) {
      $and.push({
        car: {
          $in: [
            CarLicenseEnum.LICENSE_AVAILABLE,
            CarLicenseEnum.CAR_AND_LICENSE_AVAILABLE,
          ],
        },
      });
    } else if (carValue !== null) {
      $and.push({ car: carValue });
    }
  }

  // Filter by pooler types if specified
  if (job.poolerTypes?.length && job.customer.id === customer._id) {
    $and.push({
      [`customerInformation.${customer._id}.poolerType.id`]: {
        $in: job.poolerTypes.map((poolerType) => poolerType.id),
      },
    });
  } else if (
    transfer?.criteria?.poolerTypes?.length &&
    transfer.targetCustomer.id === customer._id
  ) {
    $and.push({
      [`customerInformation.${customer._id}.poolerType.id`]: {
        $in: transfer.criteria.poolerTypes.map((poolerType) => poolerType.id),
      },
    });
  }

  const jobCriteria =
    job.customerInternalInformation?.[customer._id]?.customCriteria;
  if (jobCriteria && Object.keys(jobCriteria).length) {
    const customCriteriaFieldPath = `customerInformation.${customer._id}.customCriteria`;
    Object.keys(jobCriteria).forEach((criterionId) => {
      const criteria = jobCriteria[criterionId] as
        | CustomCriteriaOnJob
        | undefined;
      if (!criteria) return;
      if (criteria.type === CustomCriteriaType.TOGGLE) {
        let toggleOr = [];
        // If criteria is off and criteria off should not block on poolers, skip this criteria
        if (!criteria.value && !criteria.blockingToggle) {
          return;
        }

        if (criteria.value) {
          toggleOr.push({
            [`${customCriteriaFieldPath}.${criterionId}.value`]: criteria.value,
          });
        } else {
          toggleOr.push({
            [`${customCriteriaFieldPath}.${criterionId}.value`]: criteria.value,
          });
        }
        if (criteria.defaultValueOnProfile === criteria.value) {
          toggleOr.push({
            [`${customCriteriaFieldPath}.${criterionId}.value`]: {
              $exists: false,
            },
          });
        }
        $and.push({ $or: toggleOr });
      } else if (criteria.type === CustomCriteriaType.SELECT) {
        const requiredOptions = criteria.values || [];
        if (requiredOptions.length === 0) {
          return;
        }
        $and.push({
          [`${customCriteriaFieldPath}.${criterionId}.values.id`]: {
            $in: requiredOptions.map((opt) => opt.id),
          },
        });
      }
    });
  }

  if (search) {
    const searchFilters: any = [];
    search.split(' ').forEach((searchTerm) => {
      searchFilters.push({
        $or: [
          { firstName: { $regex: searchTerm, $options: 'i' } },
          { lastName: { $regex: searchTerm, $options: 'i' } },
          { email: { $regex: searchTerm, $options: 'i' } },
          { phone: { $regex: searchTerm, $options: 'i' } },
          {
            [`customerInformation.${customer._id}.poolerType.displayName`]: {
              $regex: searchTerm,
              $options: 'i',
            },
          },
        ],
      });
    });
    $and.push({ $and: searchFilters });
  }
  return { $and: $and };
}
